<template>
  <Teleport to="body">
    <div class="modal-overlay" @mousedown.self="$emit('close')">
      <dialog class="card modal-content">
        <div class="modal-header">
          <h3 class="modal-title">Создание интеграции Saby (ЭДО)</h3>
          <button class="close-btn" type="button" @click="$emit('close')">✕</button>
        </div>

        <form @submit.prevent="createIntegration" class="integration-form">
          <div class="form-body custom-scroll">
            <div class="form-group">
              <base-label for="saby-login" :required="true" :filled="!!formData.login">
                Логин
              </base-label>
              <base-input
                type="text"
                id="saby-login"
                v-model="formData.login"
                :invalid="errors.login"
                @update:modelValue="formData.login = $event || ''; errors.login = false"
              />
              <span class="error-message" v-if="errors.login">
                Поле обязательно для заполнения
              </span>
            </div>

            <div class="form-group">
              <base-label for="saby-password" :required="true" :filled="!!formData.password">
                Пароль
              </base-label>
              <base-input
                id="saby-password"
                v-model="formData.password"
                :is-password="true"
                :invalid="errors.password"
                @update:modelValue="formData.password = $event || ''; errors.password = false"
              />
              <span class="error-message" v-if="errors.password">
                Поле обязательно для заполнения
              </span>
            </div>

            <div class="form-group">
              <base-label for="saby-org-name" :filled="!!formData.org_name">
                Название нашей организации
              </base-label>
              <base-party-autocomplete
                v-model="formData.org_name"
                placeholder="Введите название или ИНН организации"
                @select="handlePartySelect"
                @update:modelValue="formData.org_name = $event || ''"
              />
            </div>

            <div class="form-group">
              <base-label
                for="saby-org-inn"
                :required="true"
                :filled="isInnFilled"
              >
                ИНН нашей организации
              </base-label>
              <base-input
                id="saby-org-inn"
                v-model="formData.org_inn"
                placeholder="10 цифр для ЮЛ или 12 для ИП"
                :invalid="errors.org_inn"
                @update:modelValue="formData.org_inn = $event || ''; errors.org_inn = false"
              />
              <span class="error-message" v-if="errors.org_inn">
                ИНН должен содержать 10 или 12 цифр
              </span>
            </div>

            <div class="form-group">
              <base-label
                for="saby-org-kpp"
                :required="requiresKpp"
                :filled="isKppFilled"
              >
                КПП нашей организации
              </base-label>
              <base-input
                id="saby-org-kpp"
                v-model="formData.org_kpp"
                placeholder="Заполняется для юрлица"
                :invalid="errors.org_kpp"
                @update:modelValue="formData.org_kpp = $event || ''; errors.org_kpp = false"
              />
              <span class="field-hint">
                Для ИП с ИНН из 12 цифр КПП не нужен.
              </span>
              <span class="error-message" v-if="errors.org_kpp">
                КПП должен содержать 9 цифр
              </span>
            </div>
          </div>

          <div class="form-actions sticky-footer">
            <base-primary-button icon="addSquare" class="form-btn" type="submit" :disabled="isSubmitting">
              <span v-if="isSubmitting">Создание...</span>
              <span v-else>Создать интеграцию</span>
            </base-primary-button>
            <base-button class="form-btn" type="button" @click="$emit('close')" :disabled="isSubmitting">
              Отмена
            </base-button>
          </div>
        </form>
      </dialog>
    </div>
  </Teleport>
</template>

<script>
import { useIntegrationStore } from "@/stores/integrationStore";

export default {
  name: "AddSabyIntegrationModal",
  emits: ["close", "action-created"],
  data() {
    return {
      isSubmitting: false,
      formData: {
        login: "",
        password: "",
        org_name: "",
        org_inn: "",
        org_kpp: "",
        provider_type: "Integration::Saby",
      },
      errors: {
        login: false,
        password: false,
        org_inn: false,
        org_kpp: false,
      },
    };
  },
  computed: {
    integrationStore() {
      return useIntegrationStore();
    },
    normalizedInn() {
      return this.normalizeDigits(this.formData.org_inn);
    },
    normalizedKpp() {
      return this.normalizeDigits(this.formData.org_kpp);
    },
    requiresKpp() {
      return this.normalizedInn.length === 10;
    },
    isInnFilled() {
      return [10, 12].includes(this.normalizedInn.length);
    },
    isKppFilled() {
      return !this.requiresKpp || this.normalizedKpp.length === 9;
    },
  },
  methods: {
    normalizeDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },

    handlePartySelect(suggestion) {
      if (!suggestion) {
        this.formData.org_name = "";
        this.formData.org_inn = "";
        this.formData.org_kpp = "";
        return;
      }

      const data = suggestion.data || {};
      this.formData.org_name =
        data.name?.short_with_opf || suggestion.value || "";
      this.formData.org_inn = data.inn || "";
      this.formData.org_kpp = data.kpp || "";
      this.errors.org_inn = false;
      this.errors.org_kpp = false;
    },

    validateForm() {
      this.errors = {
        login: false,
        password: false,
        org_inn: false,
        org_kpp: false,
      };
      let isValid = true;
      if (!String(this.formData.login || "").trim()) { this.errors.login = true; isValid = false; }
      if (!String(this.formData.password || "").trim()) { this.errors.password = true; isValid = false; }
      if (![10, 12].includes(this.normalizedInn.length)) { this.errors.org_inn = true; isValid = false; }
      if (this.normalizedKpp && this.normalizedKpp.length !== 9) { this.errors.org_kpp = true; isValid = false; }
      if (this.requiresKpp && this.normalizedKpp.length !== 9) { this.errors.org_kpp = true; isValid = false; }
      return isValid;
    },

    async createIntegration() {
      if (!this.validateForm()) return;
      try {
        this.isSubmitting = true;
        await this.integrationStore.createIntegration({
          ...this.formData,
          org_name: String(this.formData.org_name || "").trim() || null,
          org_inn: this.normalizedInn,
          org_kpp: this.normalizedKpp || null,
        });
        this.$emit("action-created");
        this.$emit("close");
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}
.modal-header {
  padding: 20px;
  border-bottom: 1px solid var(--color-grey);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  flex-shrink: 0;
}
.modal-title { margin: 0; }
.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}
.close-btn:hover { color: var(--color-dark); background: #f5f5f5; }
.integration-form { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.form-body { flex: 1; overflow-y: auto; padding: 20px; }
.custom-scroll::-webkit-scrollbar { width: 6px; }
.custom-scroll::-webkit-scrollbar-thumb { background: #ddd; border-radius: 3px; }
.error-message { display: block; margin-top: 5px; color: var(--color-danger); font-size: 14px; }
.field-hint {
  display: block;
  margin-top: 5px;
  color: #777;
  font-size: 13px;
}
.sticky-footer {
  padding: 20px;
  border-top: 1px solid var(--color-grey);
  background: #fff;
  display: flex;
  justify-content: flex-start;
  gap: 15px;
  flex-shrink: 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}
</style>
